/**
 * Marketing page navigation — vanilla JS.
 * Handles: mobile hamburger menu, tools dropdown, active link highlighting,
 * signed-in / signed-out auth swap, account dropdown menu, language dropdown.
 */

// ── i18n (Prompt 26) ────────────────────────────────────────────────────────
// Keep these in sync with web/src/lib/i18n.js. Marketing pages are static
// HTML, so this vanilla module handles both the current-language indicator
// in the globe dropdown and the language-prefixed redirect on selection.
import enMarketing from '../locales/en/marketing.json';
import frMarketing from '../locales/fr/marketing.json';
import esMarketing from '../locales/es/marketing.json';
import deMarketing from '../locales/de/marketing.json';
import itMarketing from '../locales/it/marketing.json';
import zhMarketing from '../locales/zh/marketing.json';
import jaMarketing from '../locales/ja/marketing.json';

const TRANSLATIONS = {
  en: enMarketing,
  fr: frMarketing,
  es: esMarketing,
  de: deMarketing,
  it: itMarketing,
  zh: zhMarketing,
  ja: jaMarketing,
};

const SUPPORTED_LANGS = [
  { code: 'en', native: 'English' },
  { code: 'fr', native: 'Français' },
  { code: 'es', native: 'Español' },
  { code: 'de', native: 'Deutsch' },
  { code: 'it', native: 'Italiano' },
  { code: 'zh', native: '简体中文' },
  { code: 'ja', native: '日本語' },
];
const PREFIX_CODES = SUPPORTED_LANGS.map((l) => l.code).filter((c) => c !== 'en');
const LANGUAGE_STORAGE_KEY = 'scrivar_preferred_language';

// Resolve a dotted key (e.g. "pricing.free_cta") against a nested object.
// Returns `null` if any segment is missing.
function lookup(obj, key) {
  const parts = key.split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur && typeof cur === 'object' && p in cur) cur = cur[p];
    else return null;
  }
  return typeof cur === 'string' ? cur : null;
}

// Translate a key in the current language, falling back to English if the
// key is missing in the target language. Missing-everywhere returns null.
function t(code, key) {
  const pri = TRANSLATIONS[code];
  const fallback = TRANSLATIONS.en;
  return lookup(pri, key) ?? lookup(fallback, key);
}

// Paths that have a localized variant under /{lang}/. Clicking any anchor
// pointing at one of these should keep the current locale prefix — otherwise
// /fr → /pricing drops the user into English. SPA routes (/tools, /login,
// /account, /viewer, etc.) are intentionally excluded: they self-localize
// via the logged-in account's preferred_language.
const LOCALIZED_MARKETING_PATHS = new Set(['/', '/home', '/pricing', '/download']);

function rewriteNavLinks(code) {
  if (code === 'en') return; // English already lives at root paths
  const prefix = `/${code}`;
  document.querySelectorAll('a[href]').forEach((a) => {
    const href = a.getAttribute('href');
    if (!href || !href.startsWith('/')) return;
    if (a.dataset.i18nLocalized === '1') return; // idempotent
    // Split off query + hash so we rewrite only the path portion.
    const hashIdx = href.indexOf('#');
    const queryIdx = href.indexOf('?');
    const pathEnd = Math.min(
      hashIdx === -1 ? href.length : hashIdx,
      queryIdx === -1 ? href.length : queryIdx,
    );
    const path = href.slice(0, pathEnd);
    const suffix = href.slice(pathEnd);
    if (!LOCALIZED_MARKETING_PATHS.has(path)) return;
    // `/` + prefix yields `/fr/` (keep trailing slash so the _redirects rule
    // for `/fr/` → `/home` still fires).
    const next = path === '/' ? `${prefix}/` : `${prefix}${path}`;
    a.setAttribute('href', next + suffix);
    a.dataset.i18nLocalized = '1';
  });
}

// Walks every [data-i18n] element and sets textContent from the active locale.
// data-i18n-attr="placeholder" swaps a named attribute instead of textContent.
// data-i18n-html="1" opts into innerHTML (only for translations you author).
function applyTranslations(code) {
  // <html lang=""> updates so the browser / SR reports the right language.
  if (document.documentElement.lang !== code) {
    document.documentElement.setAttribute('lang', code);
  }
  // <title data-i18n="..."> — update page title through document.title so the
  // browser tab reflects the change as well.
  const titleEl = document.querySelector('title[data-i18n]');
  if (titleEl) {
    const text = t(code, titleEl.dataset.i18n);
    if (text) document.title = text;
  }
  document.querySelectorAll('[data-i18n]').forEach((el) => {
    if (el.tagName === 'TITLE') return; // handled above
    const key = el.dataset.i18n;
    if (!key) return;
    const text = t(code, key);
    if (text == null) return;
    const attr = el.dataset.i18nAttr;
    if (attr) {
      el.setAttribute(attr, text);
    } else if (el.dataset.i18nHtml === '1') {
      // Only used for translator-authored strings (no user input).
      el.innerHTML = text;
    } else {
      el.textContent = text;
    }
  });
}

function currentLanguageFromPath() {
  const first = window.location.pathname.split('/').filter(Boolean)[0];
  if (first && PREFIX_CODES.includes(first)) return first;
  return 'en';
}

function stripLocalePrefix(pathname) {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length && PREFIX_CODES.includes(parts[0])) {
    return '/' + parts.slice(1).join('/');
  }
  return pathname;
}

function buildLocalizedPath(pathname, code) {
  const base = stripLocalePrefix(pathname) || '/';
  if (code === 'en') return base;
  if (base === '/') return `/${code}/`;
  return `/${code}${base}`;
}

function installLanguageDropdown() {
  // Install into every slot element (nav AND footer). Supports the legacy
  // `#nav-language-slot` id plus any element with `data-language-slot`.
  const slots = Array.from(
    document.querySelectorAll('#nav-language-slot, [data-language-slot]')
  );
  if (slots.length === 0) return;
  slots.forEach((slot, idx) => installLanguageDropdownInto(slot, idx));
}

function installLanguageDropdownInto(slot, idx) {
  const current = currentLanguageFromPath();
  const currentEntry = SUPPORTED_LANGS.find((l) => l.code === current) || SUPPORTED_LANGS[0];

  const wrapper = document.createElement('div');
  wrapper.className = 'relative inline-block';

  const btn = document.createElement('button');
  btn.id = idx === 0 ? 'nav-language-btn' : `nav-language-btn-${idx}`;
  btn.type = 'button';
  // Footer variant gets a darker-background styling via data attribute.
  const isFooter = slot.dataset.languageSlot === 'footer';
  btn.className = isFooter
    ? 'inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium text-gray-400 hover:text-white transition-colors'
    : 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors';
  btn.setAttribute('aria-haspopup', 'menu');
  btn.setAttribute('aria-expanded', 'false');

  const svgNs = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNs, 'svg');
  svg.setAttribute('width', isFooter ? '13' : '16');
  svg.setAttribute('height', isFooter ? '13' : '16');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  const circle = document.createElementNS(svgNs, 'circle');
  circle.setAttribute('cx', '12');
  circle.setAttribute('cy', '12');
  circle.setAttribute('r', '10');
  const line = document.createElementNS(svgNs, 'line');
  line.setAttribute('x1', '2');
  line.setAttribute('y1', '12');
  line.setAttribute('x2', '22');
  line.setAttribute('y2', '12');
  const curve = document.createElementNS(svgNs, 'path');
  curve.setAttribute('d', 'M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z');
  svg.appendChild(circle);
  svg.appendChild(line);
  svg.appendChild(curve);
  btn.appendChild(svg);

  const label = document.createElement('span');
  label.textContent = currentEntry.native;
  btn.appendChild(label);

  const menu = document.createElement('div');
  menu.id = idx === 0 ? 'nav-language-menu' : `nav-language-menu-${idx}`;
  menu.className = isFooter
    ? 'hidden absolute right-0 bottom-full mb-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50'
    : 'hidden absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50';
  menu.setAttribute('role', 'menu');

  SUPPORTED_LANGS.forEach((l) => {
    const item = document.createElement('button');
    item.type = 'button';
    item.setAttribute('role', 'menuitem');
    item.dataset.langCode = l.code;
    item.textContent = l.native;
    item.className = l.code === current
      ? 'w-full text-left px-3 py-2 text-sm font-semibold text-indigo-600'
      : 'w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors';
    item.addEventListener('click', () => {
      try { localStorage.setItem(LANGUAGE_STORAGE_KEY, l.code); } catch {}
      const target = buildLocalizedPath(window.location.pathname, l.code);
      window.location.href = target + window.location.search + window.location.hash;
    });
    menu.appendChild(item);
  });

  wrapper.appendChild(btn);
  wrapper.appendChild(menu);
  slot.appendChild(wrapper);

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    menu.classList.toggle('hidden');
    btn.setAttribute('aria-expanded', String(!menu.classList.contains('hidden')));
  });
  document.addEventListener('click', (e) => {
    if (!wrapper.contains(e.target)) {
      menu.classList.add('hidden');
      btn.setAttribute('aria-expanded', 'false');
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      menu.classList.add('hidden');
      btn.setAttribute('aria-expanded', 'false');
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  // Marketing nav language dropdown — opt-in per page via a `#nav-language-slot`
  // element inside the nav. Falls through silently if the slot isn't present.
  // Apply translations before the dropdown installs so the current-language
  // label in the globe menu matches what the rest of the page is showing.
  const activeLang = currentLanguageFromPath();
  applyTranslations(activeLang);
  rewriteNavLinks(activeLang);
  installLanguageDropdown();
  // ── Auth state: swap signed-out / signed-in blocks based on localStorage ──
  function readSession() {
    try {
      const raw = localStorage.getItem('scrivar_session');
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || !data.email) return null;
      return data;
    } catch {
      return null;
    }
  }

  const session = readSession();
  const authState = session ? 'signed-in' : 'signed-out';

  // Inject a rule so the non-matching state is hidden regardless of any
  // responsive utility classes. This avoids fighting Tailwind's `hidden sm:flex`
  // pattern with JS class toggling.
  if (!document.getElementById('auth-visibility-rule')) {
    const style = document.createElement('style');
    style.id = 'auth-visibility-rule';
    style.textContent = `
      [data-auth-state="signed-in"] [data-auth="signed-out"],
      [data-auth-state="signed-out"] [data-auth="signed-in"] {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }
  document.documentElement.setAttribute('data-auth-state', authState);

  // The signed-in blocks ship with `hidden` so they stay hidden if this script
  // never runs (belt-and-suspenders). Strip it now that the rule above handles
  // visibility.
  document.querySelectorAll('[data-auth="signed-in"]').forEach((el) => {
    el.classList.remove('hidden');
  });

  if (session) {
    const email = session.email || '';
    const initial = (email[0] || '?').toUpperCase();
    document.querySelectorAll('[data-auth-email]').forEach((el) => {
      el.textContent = email;
    });
    document.querySelectorAll('[data-auth-initial]').forEach((el) => {
      el.textContent = initial;
    });
  }

  // Account dropdown (desktop)
  const accountBtn = document.getElementById('nav-account-btn');
  const accountMenu = document.getElementById('nav-account-menu');
  if (accountBtn && accountMenu) {
    accountBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const open = !accountMenu.classList.contains('hidden');
      accountMenu.classList.toggle('hidden');
      accountBtn.setAttribute('aria-expanded', String(!open));
    });
    document.addEventListener('click', (e) => {
      if (!accountBtn.contains(e.target) && !accountMenu.contains(e.target)) {
        accountMenu.classList.add('hidden');
        accountBtn.setAttribute('aria-expanded', 'false');
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        accountMenu.classList.add('hidden');
        accountBtn.setAttribute('aria-expanded', 'false');
      }
    });
  }

  // Sign-out (desktop + mobile) — clear session cache, hand off to /login
  // which will finalize Firebase signOut via the ?signout=1 param.
  document.querySelectorAll('[data-auth-signout]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      try { localStorage.removeItem('scrivar_session'); } catch {}
      window.location.href = '/login?signout=1';
    });
  });

});
